import { loader } from 'graphql.macro';
import UniversalDropdown from '../../components/dropdownV2/UniversalDropdown';
import { Text } from '../../components/typography';
import { useModal } from '../../providers/ModalProvider';
import { modals } from '../../providers/modals';
import styles from './RiskRegistrySelector.module.scss';
import { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import TextWithIcon from '../../components/typography/TextWithIcon';
import { useTranslation } from 'react-i18next';
import { keys } from '../../utilities/translator/translation_keys';
import { Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { paths } from '../../constants/strings';
import { useRiskLinkActionItems } from '../../hooks/risk_registry/risk_link_action_items';

const registryCategoryQuery = loader(
  'src/pages/risk_registry/NewRiskList.registry.graphql',
);
const addAssociationMutation = loader(
  './RiskRegistrySelector.associate.graphql',
);
const removeAssociationMutation = loader(
  './RiskRegistrySelector.disassociate.graphql',
);
const existingQuery = loader('./RiskRegistrySelector.existing.graphql');

export default function RiskRegistrySelector({
  isEditing,
  onSubmit = () => {},
  size = 'md',
  readOnly,
  modelId = null,
  modelType = null,
  hideTitle = false,
}) {
  const navigate = useNavigate();
  const [selectedCategories, setSelectedCategories] = useState([]);
  const { openModal } = useModal();
  const [addAssociation, { loading: addLoading }] = useMutation(
    addAssociationMutation,
  );
  const [removeAssociation, { loading: removeLoading }] = useMutation(
    removeAssociationMutation,
  );
  const { createLinkActionItems, archiveLinkActionItems } =
    useRiskLinkActionItems();
  const { refetch: refetchRisks, data: { riskRegistry: risks = [] } = {} } =
    useQuery(existingQuery, {
      skip: !modelId,
      variables: { modelId: `${modelId}`, association: `${modelType}s` },
      onCompleted: () => setSelectedCategories(risks),
    });
  const { data: { riskRegistry = [] } = {} } = useQuery(registryCategoryQuery);
  const { t } = useTranslation();
  const categoryOptions = riskRegistry.map((r) => ({
    ...r,
    title: r.category,
    value: r.id,
    id: r.id,
    label: r.category,
  }));

  const handleChange = useCallback(
    async (newValues) => {
      const selectedIds = selectedCategories?.map(({ id }) => id);
      const newIds = newValues?.map(({ id, value }) => id ?? value);

      if (newIds?.length > selectedIds?.length) {
        const idToAdd = newIds?.filter((id) => !selectedIds.includes(id))[0];
        await addAssociation({
          variables: {
            riskRegistryId: idToAdd,
            modelId,
            modelType,
          },
        });
        await createLinkActionItems({
          riskRegistryId: idToAdd,
          modelId,
          modelType,
        });
      } else {
        const idToRemove = selectedIds?.filter((id) => !newIds.includes(id))[0];
        await removeAssociation({
          variables: {
            riskRegistryId: idToRemove,
            modelId,
            modelType,
          },
        });
        await archiveLinkActionItems({
          riskRegistryId: idToRemove,
          modelId,
          modelType,
        });
      }
      setSelectedCategories(newValues);
      refetchRisks();
      onSubmit(newValues);
    },
    [
      selectedCategories,
      addAssociation,
      removeAssociation,
      modelId,
      modelType,
      onSubmit,
      refetchRisks,
      createLinkActionItems,
      archiveLinkActionItems,
    ],
  );

  const loading = useMemo(
    () => addLoading || removeLoading,
    [addLoading, removeLoading],
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        {!hideTitle ? (
          <div className={styles.flexRow}>
            <Text size={size} weight="semiBold" noMargin>
              Associated Risk Categories
            </Text>
            {loading && <Spinner className={styles.spinner} size="sm" />}
          </div>
        ) : (
          <div> </div>
        )}
        {isEditing && !readOnly && (
          <TextWithIcon
            color="accentPrimary"
            weight="semiBold"
            size="sm"
            noMargin
            leftIcon="add"
            hover
            onClick={() =>
              openModal({
                modalName: modals.addRiskRegistry,
              })
            }
          >
            {t(keys.common.NEW)}
          </TextWithIcon>
        )}
      </div>
      {isEditing && !readOnly ? (
        <UniversalDropdown
          type="risk"
          isMulti
          selected={selectedCategories}
          isClearable={true}
          options={categoryOptions}
          closeMenuOnSelect={false}
          onChange={async (vals) => {
            await handleChange(vals);
          }}
        />
      ) : risks?.length > 0 ? (
        risks?.map((r) => (
          <div className={styles.risk}>
            <Text
              marginSm
              weight="semiBold"
              color="accentPrimary"
              hover
              onClick={() =>
                navigate(`/${paths.registry}/${r?.id}?section=DETAILS`)
              }
            >
              {r.category || r.description || 'Untitled Risk'}
            </Text>
          </div>
        ))
      ) : (
        <Text marginSm weight="semiBold" color="secondary">
          {t(keys.common.NONE)}
        </Text>
      )}
    </div>
  );
}
